import React, { useEffect, useState } from "react";
import { X, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS_FLOW = [
  { key: "new",                    label: "New" },
  { key: "reviewing",              label: "Reviewing" },
  { key: "waiting_customer",       label: "Waiting for Customer" },
  { key: "availability_confirmed", label: "Availability Confirmed" },
  { key: "quote_ready",            label: "Quote Ready" },
  { key: "invoice_sent",           label: "Invoice Sent" },
  { key: "paid",                   label: "Paid" },
  { key: "preparing",              label: "Preparing" },
  { key: "completed",              label: "Completed" },
  { key: "cancelled",              label: "Cancelled" },
];

function formatWhen(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

function statusLabel(k) {
  return STATUS_FLOW.find((s) => s.key === k)?.label || k;
}

export default function RequestDetail({ requestId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/admin/sticker-requests/${requestId}`).then((r) => {
      setData(r.data);
      setNotes(r.data.request.admin_notes || "");
      setAssignee(r.data.request.assigned_to || "");
    });

  useEffect(() => { if (requestId) load(); }, [requestId]);

  if (!requestId) return null;

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/admin/sticker-requests/${requestId}/details`, {
        admin_notes: notes,
        assigned_to: assignee || null,
        ...extra,
      });
      toast.success("Request updated");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const setStatus = (status) => save({ status });

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex justify-end" data-testid="request-drawer">
      <div className="w-full max-w-3xl bg-white border-l-2 border-[var(--ink)] h-full overflow-y-auto">
        <div className="sticky top-0 bg-[var(--ink)] text-white px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--panini-yellow)] border-2 border-white flex items-center justify-center">
              <Send size={16} className="text-[var(--ink)]" />
            </div>
            <div>
              <div className="font-display text-lg">{data?.request?.full_name || "Request"}</div>
              <div className="text-xs text-white/70 font-mono">{data?.request?.request_number}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="request-drawer-close"><X size={18} /></button>
        </div>

        {!data ? <div className="p-8 text-[var(--smoke)]">Loading…</div> : (
          <div className="p-6 space-y-6">
            {/* Status pipeline */}
            <div>
              <div className="kicker kicker-red text-[11px] mb-3">Status</div>
              <div className="flex flex-wrap gap-2" data-testid="status-pipeline">
                {STATUS_FLOW.map((s) => {
                  const active = data.request.status === s.key;
                  return (
                    <button key={s.key} onClick={() => setStatus(s.key)} disabled={busy || active}
                      data-testid={`status-set-${s.key}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
                        active
                          ? "border-[var(--ink)] bg-[var(--panini-yellow)] text-[var(--ink)]"
                          : "border-[var(--line)] bg-white text-[var(--ink)] hover:border-[var(--ink)]"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Two columns */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Info label="Email" value={data.request.email} />
              <Info label="Phone" value={data.request.phone} />
              <Info label="State / City" value={`${data.request.state || "—"} / ${data.request.city || "—"}`} />
              <Info label="Album" value={data.request.collection || data.request.album_id || "—"} />
              <Info label="Fulfillment" value={(data.request.fulfillment_method || "").replace(/_/g, " ")} />
              <Info label="Preferred contact" value={data.request.preferred_contact || "—"} />
              <Info label="Instagram" value={data.request.instagram || "—"} />
              <Info label="TikTok" value={data.request.tiktok || "—"} />
              <Info label="Submitted" value={formatWhen(data.request.created_at)} />
              <Info label="Last updated" value={formatWhen(data.request.updated_at)} />
            </div>

            {/* Missing list */}
            <div>
              <div className="kicker kicker-black text-[11px] mb-2">Missing list (verbatim)</div>
              <pre className="p-4 bg-[var(--ink)] text-white rounded-xl whitespace-pre-wrap text-sm font-mono max-h-64 overflow-auto" data-testid="request-missing-list">{data.request.missing_list}</pre>
            </div>

            {/* Customer notes (from submission) */}
            {data.request.notes && (
              <div>
                <div className="kicker kicker-black text-[11px] mb-2">Customer notes</div>
                <div className="p-3 rounded-xl bg-[var(--panini-yellow-soft)] text-sm whitespace-pre-wrap">{data.request.notes}</div>
              </div>
            )}

            {/* Assignment + Internal notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <div className="kicker kicker-black text-[11px] mb-2">Assigned to (admin id, optional)</div>
                <input value={assignee} onChange={(e) => setAssignee(e.target.value)} className="gm-field" data-testid="request-assignee"
                       placeholder="admin user_id — leave blank for unassigned" />
              </label>
              <div className="block">
                <div className="kicker kicker-black text-[11px] mb-2">Actions</div>
                <button onClick={() => save()} disabled={busy} data-testid="request-save"
                  className="btn-red text-sm">
                  {busy ? "Saving…" : "Save notes & assignment"} <ArrowRight size={14} />
                </button>
              </div>
            </div>

            <div>
              <div className="kicker kicker-black text-[11px] mb-2">Internal notes (never shown to customer)</div>
              <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="gm-field" data-testid="request-internal-notes" />
            </div>

            {/* Activity history */}
            <div>
              <div className="kicker kicker-red text-[11px] mb-3">Activity history ({data.activity.length})</div>
              {data.activity.length === 0 ? (
                <div className="text-[var(--smoke)] text-sm">No activity yet — status changes and edits will appear here.</div>
              ) : (
                <ol className="border-l-2 border-[var(--ink)] pl-5 space-y-3" data-testid="request-activity">
                  {data.activity.map((a) => (
                    <li key={a.id} className="relative">
                      <span className="absolute -left-[27px] top-1 w-4 h-4 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)]" />
                      <div className="flex justify-between gap-3">
                        <div className="font-mono text-xs font-bold text-[var(--panini-red)]">{a.action}</div>
                        <div className="text-[11px] text-[var(--smoke)] whitespace-nowrap">{formatWhen(a.created_at)}</div>
                      </div>
                      {a.action?.startsWith("sticker_request.status.") && (
                        <div className="text-xs mt-0.5">→ {statusLabel(a.action.split(".").pop())}</div>
                      )}
                      {a.ip_address && <div className="text-[10px] text-[var(--smoke)]">from {a.ip_address}</div>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="kicker kicker-black text-[10px]">{label}</div>
      <div className="text-[var(--ink)] mt-1 break-words">{value}</div>
    </div>
  );
}

export { STATUS_FLOW, statusLabel };
